import React, { useState } from "react";
import { ReactComponent as Close } from "assets/svgs/close.svg";
import { shareWishlistViaEmail, markListPublic } from "services/api/swym";
import Modal from "components/modal";
import { toast } from "react-toastify";
import { ReactComponent as LinkIcon } from "assets/svgs/fi-rs-link.svg";
import { useCustomer } from "hooks/useStore";
import Link from "components/link";
import clsx from "clsx/lite";

const ShareListModal = ({ isOpen, toggle, list }) => {
	const { isAuthenticated } = useCustomer(["isAuthenticated"]);
	const [senderName, setSenderName] = useState(list.userinfo?.fname);
	const [message, setMessage] = useState();
	const [recipientEmail, setRecipientEmail] = useState("");
	const [disabled, setDisabled] = useState(false);
	const [errors, setErrors] = useState({
		senderName: "",
		recipientEmail: "",
	});

	const onChange = (e) => {
		const field = e.target.id;
		const value = e.target.value;

		switch (field) {
			case "sender-name":
				setSenderName(value);
				setErrors((prevErrors) => ({
					...prevErrors,
					senderName: value.trim() ? "" : "Please enter a name.",
				}));
				break;
			case "recipient-email":
				setRecipientEmail(value);
				setErrors((prevErrors) => ({
					...prevErrors,
					recipientEmail: value.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "" : "Please enter a valid email.",
				}));
				break;
			case "message":
				setMessage(value);
				break;
			default:
				console.log("Invalid target");
		}
	};

	const submitDetails = async () => {
		setDisabled(true);

		// Reset errors
		setErrors({
			senderName: "",
			recipientEmail: "",
		});

		let hasError = false;
		const newErrors = {};

		if (!senderName.trim()) {
			newErrors.senderName = "Please enter a name.";
			hasError = true;
		}

		if (!recipientEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
			newErrors.recipientEmail = "Please enter a valid email.";
			hasError = true;
		}

		if (hasError) {
			setErrors(newErrors);
			setDisabled(false);
			return;
		}
		const { error, msg } = await shareWishlistViaEmail(list.lid, recipientEmail, senderName);
		if (error) {
			toast.error(msg);
		} else {
			toast.success(msg);
			toggle();
		}
		setDisabled(false);
	};

	const callCopyLink = async () => {
		const { error, msg } = await markListPublic(list.lid);
		if (!error) {
			const sharedurl = `${window.location.origin}/wishlist?sharedlid=${list.lid}`;
			navigator.clipboard.writeText(sharedurl);
			toast.success("Link copied to clipboard");
		}
		else {
			toast.error(msg);
		}
		return toggle();
	};

	if (!isAuthenticated) {
		return (
			<Modal
				isOpen={isOpen}
				close={toggle}
				hideHeader
				className="text-center !w-full md:!max-w-[408px]"
			>
				<div className="p-5 flex flex-col gap-8">
					<div className="flex w-full justify-between">
						<h5>Login to Share your Wishlist</h5>
						<button className="p-1 ml-auto mb-auto" onClick={toggle}>
							<Close className="size-5" />
						</button>
					</div>
					<div className="flex flex-col gap-2 items-start">
						<p>You are currently shopping anonymously.</p>
						<p>Please login to share your Wishlist.</p>
					</div>
					<Link to="/account/login" className="btn-primary w-full">Login</Link>
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			close={toggle}
			hideHeader
			label="Share Wishlist"
			className="text-center !w-full md:!max-w-[408px]"
		>
			<div className="p-5 flex flex-col gap-6">
				<div className="w-full flex justify-between">
					<h4 className="font-medium">Share List</h4>
					<button className="p-1 ml-auto mb-auto" onClick={toggle}>
						<Close className="size-5" />
					</button>
				</div>
				<div className="flex flex-col gap-5">
					<div className="flex flex-col gap-2">
						<div
							className="flex flex-col border bg-white w-full group outline-beige focus-within:outline"
							tabIndex="0">
							<label htmlFor="senderName" className="text-xs px-4 pt-3 text-left">Sender Name</label>
							<input
								onChange={(e) => onChange(e)}
								id="sender-name"
								type="text"
								value={senderName}
								placeholder="Wishlist"
								className="w-full h-8 rounded-md border -mt-1 border-white px-4 text-sm outline-none" />
						</div>
						{errors.senderName && <span className="text-red-600 text-xs text-left">*{errors.senderName}</span>}
					</div>
					<div className="flex flex-col gap-2">
						<div
							className="flex flex-col border bg-white w-full group outline-beige focus-within:outline"
							tabIndex="0">
							<label htmlFor="recepientsEmail"
								className={
									clsx("text-xs px-4 pt-3 text-left", errors.recipientEmail && "text-red-600")}>
								Recipients Email*
							</label>
							<input
								onChange={(e) => onChange(e)}
								id="recipient-email"
								type="email"
								required
								placeholder="user@gmail.com"
								value={recipientEmail}
								className="w-full h-8 rounded-md border -mt-1 border-white px-4 text-sm outline-none" />
						</div>
						{errors.recipientEmail &&
							<span className="text-red-600 text-xs text-left">*{errors.recipientEmail}</span>}
					</div>
					<div className="flex flex-col border bg-white w-full group outline-beige focus-within:outline" tabIndex="0">
						<label htmlFor="message" className="text-xs px-4 pt-3 text-left">Message</label>
						<textarea
							onChange={(e) => onChange(e)}
							id="message"
							value={message}
							placeholder={`Hey there! Check out ${list.lname}`}
							rows={5}
							className="w-full rounded-md border -mt-1 border-white px-4 py-2 text-sm outline-none resize-none" />
					</div>
					<div className="flex w-full gap-2 pb-6 border-b">
						<button onClick={submitDetails} className="btn-primary w-full" disabled={disabled}>Share List</button>
					</div>
					<div className="flex flex-col">
						<p className="text-sm text-left text-secondary">Or share via</p>
					</div>
					<div>
						<button onClick={callCopyLink}
							className="flex gap-1 px-4 py-2 border items-center rounded-md">
							<LinkIcon /> Copy Link
						</button>
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default ShareListModal;